/**
 * RFC 1881 Zori Editing — ClinicalImpression extension URLs and Langfuse score
 * names aligned with agents `fountain_agents.lib.constants` / `lib.feedback`.
 *
 * RFC 1885 (Zori KB) stresses observability for Zori surfaces; recording hunk
 * decisions as Langfuse scores on the **suggestion** trace supports retrieval
 * quality and product analytics.
 */
/** Root Langfuse trace id for the suggest-agent run that produced the draft diff */
export const FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION =
  'https://lifeomic.com/fhir/zori-suggestion-langfuse-trace-id';

export const LANGFUSE_SCORE_SUGGESTION_HUNK_ACCEPTED =
  'suggestion-hunk-accepted';

export const LANGFUSE_SCORE_SUGGESTION_HUNK_REJECTED =
  'suggestion-hunk-rejected';

export type SuggestionHunkDecision = 'accepted' | 'rejected';

/** `agent_name` for `POST /v1/agents-v2/feedback` when scoring suggest-agent runs. */
export const SUGGEST_AGENT_NAME = 'suggest-agent';

export type SuggestionScoreFeedbackInput = {
  agentName: string;
  traceId: string;
  feedback: string;
  score: number;
  scoreName: string;
};

/**
 * Payload fields for `POST /v1/agents-v2/feedback` when logging per-hunk review
 * on the suggest-agent Langfuse trace (see impression extension
 * {@link FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION}).
 */
export function buildSuggestionHunkFeedbackPayload(
  decision: SuggestionHunkDecision,
  detail?: string,
): { score: number; score_name: string; feedback: string } {
  const score = decision === 'accepted' ? 5 : 1;
  const score_name =
    decision === 'accepted'
      ? LANGFUSE_SCORE_SUGGESTION_HUNK_ACCEPTED
      : LANGFUSE_SCORE_SUGGESTION_HUNK_REJECTED;
  const suffix = detail?.trim() ? `: ${detail.trim()}` : '';
  const feedback =
    decision === 'accepted'
      ? `Zori suggestion hunk accepted${suffix}`
      : `Zori suggestion hunk rejected${suffix}`;
  return { score, score_name, feedback };
}

/**
 * Fire-and-forget Langfuse score on the suggestion trace (no-op without trace id).
 * Swallows errors so diff UX is never blocked by telemetry.
 */
export function fireSuggestionTraceScore(
  send: (input: SuggestionScoreFeedbackInput) => Promise<unknown>,
  suggestionTraceId: string | undefined,
  decision: SuggestionHunkDecision,
  detail?: string,
): void {
  if (!suggestionTraceId?.trim()) {
    return;
  }
  const { score, score_name, feedback } = buildSuggestionHunkFeedbackPayload(
    decision,
    detail,
  );
  void send({
    agentName: SUGGEST_AGENT_NAME,
    traceId: suggestionTraceId.trim(),
    feedback,
    score,
    scoreName: score_name,
  }).catch(() => {
    /* telemetry only */
  });
}
